import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { SearchResult } from '@shared/models/search-section.model';
import { CurrentUserService } from '@shared/services/current-user.service';
import { HeaderService } from '@shared/services/header.service';
import { SearchService } from '@shared/services/search.service';
import { catchError, map, of, startWith, switchMap } from 'rxjs';
import {
  Collection,
  InvestmentJurisdiction,
} from 'src/app/modules/admin/models';
import { InvestmentJurisdictionService } from 'src/app/modules/admin/services';

const NO_COUNTRIES: Collection<InvestmentJurisdiction> = {
  total: 0,
  items: [],
};

export type GlobalSearchVariant = 'full' | 'compact';

@Component({
  standalone: false,
  selector: 'gstar-global-search',
  templateUrl: './global-search.component.html',
  styleUrl: './global-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalSearchComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly headerService = inject(HeaderService);
  private readonly investmentJurisdictionService = inject(
    InvestmentJurisdictionService
  );
  private readonly searchService = inject(SearchService);

  /** `full` shows light search suggestions, `compact` only triggers the deep search (simlpier). */
  variant: InputSignal<GlobalSearchVariant> =
    input<GlobalSearchVariant>('full');

  searchText: WritableSignal<string> = signal('');

  private readonly queryParam: Signal<string> = toSignal(
    this.route.queryParamMap.pipe(map(params => params.get('q') ?? '')),
    { initialValue: '' }
  );

  // Same source as the country dropdown in the sidebar: only the ones accessible to the client.
  private readonly countries: Signal<Collection<InvestmentJurisdiction>> =
    toSignal(
      this.headerService.selectedClient$.pipe(
        startWith(this.headerService.selectedClient()),
        switchMap(client =>
          client
            ? this.investmentJurisdictionService
                .getInvestmentJurisdictionsForNav()
                .pipe(catchError(() => of(NO_COUNTRIES)))
            : of(NO_COUNTRIES)
        )
      ),
      { initialValue: NO_COUNTRIES }
    );

  isFullVariant: Signal<boolean> = computed(() => this.variant() === 'full');

  hasSearchQuery: Signal<boolean> = computed(
    () => this.searchText().trim().length >= 2
  );

  searchResults: Signal<SearchResult[]> = computed(() =>
    this.isFullVariant()
      ? this.searchService.lightSearch(
          this.searchText(),
          this.countries().items,
          this.currentUserService.isAdmin()
        )
      : []
  );

  showDeepSearchTrigger: Signal<boolean> = computed(
    () =>
      this.isFullVariant() &&
      this.hasSearchQuery() &&
      this.searchResults().length > 0
  );

  showNoSearchResults: Signal<boolean> = computed(
    () =>
      this.isFullVariant() &&
      this.hasSearchQuery() &&
      this.searchResults().length === 0
  );

  constructor() {
    effect(() => {
      if (this.isFullVariant()) return;
      this.searchText.set(this.queryParam());
    });
  }

  navigateToResult(route: string): void {
    this.searchText.set('');
    this.router.navigate([route]);
  }

  /**
   * Runs the deep search by navigating to the results page with the query.
   */
  onSearchKeydown(): void {
    const query = this.searchText().trim();
    if (query.length < 2) return;

    this.router.navigate(['/dashboard/search-results'], {
      queryParams: { q: query },
    });
  }
}
